import React from 'react';import ReactDOM from 'react-dom';import { useNavigate } from 'react-router-dom';import API_BASE_URL from '../config';import styles from '../css-isolated/su_activity_logs.module.css';import ChangePasswordModal from './change_pass';
import authFetch from '../utils/authFetch';

function SuActivityLogs() {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = React.useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [userData, setUserData] = React.useState(null);
  const [isDarkMode, setIsDarkMode] = React.useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || savedTheme === null;
  });
  const [showThemeModal, setShowThemeModal] = React.useState(false);
  const [pendingTheme, setPendingTheme] = React.useState(null);
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [showErrorModal, setShowErrorModal] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [activityLogs, setActivityLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterType, setFilterType] = React.useState('all'); // all, consultation, health_record
  const [dateFilter, setDateFilter] = React.useState('all'); // all, today, week, month
  const [specificDate, setSpecificDate] = React.useState('');
  const [dateRangeFrom, setDateRangeFrom] = React.useState('');
  const [dateRangeTo, setDateRangeTo] = React.useState('');

  // Date modal state
  const [isDateModalOpen, setIsDateModalOpen] = React.useState(false);
  const [modalMonth, setModalMonth] = React.useState('');
  const [modalDay, setModalDay] = React.useState('');
  const [modalYear, setModalYear] = React.useState('');
  const [modalTextDate, setModalTextDate] = React.useState('');
  const [modalError, setModalError] = React.useState('');
  const [modalType, setModalType] = React.useState(''); // 'specific', 'from', or 'to'

  // Helper function to get days in month
  const getDaysInMonth = (month, year) => {
    if (!month) return 31;
    const monthNum = parseInt(month);
    if (monthNum === 2) {
      if (!year) return 29;
      const yearNum = parseInt(year);
      // Leap year calculation
      return (yearNum % 4 === 0 && yearNum % 100 !== 0) || (yearNum % 400 === 0) ? 29 : 28;
    }
    // April, June, September, November have 30 days
    if ([4, 6, 9, 11].includes(monthNum)) return 30;
    return 31;
  };

  // Helper function to format nurse name
  const formatNurseName = (account) => {
    if (!account) return 'Unknown';
    const { last_name, first_name, middle_name } = account;
    
    const lastName = last_name ? last_name.charAt(0).toUpperCase() + last_name.slice(1).toLowerCase() : '';
    const firstName = first_name ? first_name.charAt(0).toUpperCase() + first_name.slice(1).toLowerCase() : '';
    const middleInitial = middle_name ? middle_name.charAt(0).toUpperCase() + '.' : '';
    
    return `${lastName}, ${firstName} ${middleInitial}`.trim().replace(/,$/, '');
  };
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 10;

  // Load user data on mount
  React.useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUserData(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }, []);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch activity logs from backend
  React.useEffect(() => {
    const fetchActivityLogs = async () => {
      try {
        setLoading(true);
        // Fetch health consult, health record, and final remarks activities
        const [consultResponse, recordResponse, remarksResponse] = await Promise.all([
          authFetch(`${API_BASE_URL}/api/health-consult`, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }),
          authFetch(`${API_BASE_URL}/api/hr-info`, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }),
          authFetch(`${API_BASE_URL}/api/hr-final-remarks/logs`, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          })
        ]);

        // Check response status
        if (!consultResponse.ok) {
          throw new Error(`Health consult API error: ${consultResponse.status}`);
        }
        if (!recordResponse.ok) {
          throw new Error(`Health records API error: ${recordResponse.status}`);
        }
        if (!remarksResponse.ok) {
          throw new Error(`Final remarks API error: ${remarksResponse.status}`);
        }

        const consultData = await consultResponse.json();
        const recordData = await recordResponse.json();
        const remarksData = await remarksResponse.json();
        // Combine and format activity logs
        const logs = [];

        // Process consultations (include entries even without NOD/MD info)
        if (consultData.data) {
          consultData.data.forEach(consult => {
            if (consult.created_at) {
              logs.push({
                id: `consult-create-${consult.id}`,
                type: 'consultation',
                action: 'created',
                recordId: consult.id,
                nurseName: consult.account_creator ? formatNurseName(consult.account_creator) : 'N/A',
                timestamp: consult.created_at,
                details: `Health Consultation #${consult.id} - ${consult.chief_complaint || 'N/A'}`,
                patientInfo: consult.hr_info || null
              });
            }
            if (shouldShowUpdate(consult.created_at, consult.updated_at)) {
              logs.push({
                id: `consult-update-${consult.id}`,
                type: 'consultation',
                action: 'updated',
                recordId: consult.id,
                nurseName: consult.account_updater ? formatNurseName(consult.account_updater) : 'N/A',
                timestamp: consult.updated_at,
                details: `Health Consultation #${consult.id} - ${consult.chief_complaint || 'N/A'}`,
                patientInfo: consult.hr_info || null
              });
            }
          });
        }

        // Process health records (include entries even without nurse info)
        if (Array.isArray(recordData)) {
          recordData.forEach(record => {
            if (record.created_at) {
              logs.push({
                id: `record-create-${record.id}`,
                type: 'health_record',
                action: 'created',
                recordId: record.id,
                nurseName: record.nurse_created ? formatNurseName(record.nurse_created) : 'N/A',
                timestamp: record.created_at,
                details: `Health Record - ${record.first_name} ${record.last_name}`,
                patientInfo: record
              });
            }
            if (shouldShowUpdate(record.created_at, record.updated_at)) {
              logs.push({
                id: `record-update-${record.id}`,
                type: 'health_record',
                action: 'updated',
                recordId: record.id,
                nurseName: record.nurse_updated ? formatNurseName(record.nurse_updated) : 'N/A',
                timestamp: record.updated_at,
                details: `Health Record - ${record.first_name} ${record.last_name}`,
                patientInfo: record
              });
            }
          });
        }

        // Process final remarks
        if (Array.isArray(remarksData)) {
          remarksData.forEach(remark => {
            if (remark.created_at) {
              const hrInfo = remark.hr_info;
              const nurseName = hrInfo?.nurse_updated 
                ? formatNurseName(hrInfo.nurse_updated) 
                : (hrInfo?.nurse_created ? formatNurseName(hrInfo.nurse_created) : 'N/A');
              logs.push({
                id: `remark-${remark.id}`,
                type: 'final_remarks',
                action: remark.final_remarks || 'Updated',
                recordId: remark.id,
                nurseName: nurseName,
                timestamp: remark.created_at,
                details: `Final Remarks - ${remark.final_remarks}`,
                patientInfo: hrInfo || null
              });
            }
          });
        }

        // Sort by timestamp (newest first)
        logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        setActivityLogs(logs);
      } catch (error) {
        console.error('Error fetching activity logs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivityLogs();
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    localStorage.removeItem('user');
    setShowLogoutModal(false);
    navigate('/login-superuser');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return formatDate(timestamp);
  };

  // Helper: only show update when timestamps differ
  const shouldShowUpdate = (createdAt, updatedAt) => {
    if (!updatedAt) return false;
    if (!createdAt) return true; // if no created timestamp, treat as update
    const c = new Date(createdAt).getTime();
    const u = new Date(updatedAt).getTime();
    return Number.isFinite(c) && Number.isFinite(u) ? u !== c : updatedAt !== createdAt;
  };

  // Filter activities
  const filteredLogs = React.useMemo(() => {
    let filtered = activityLogs;

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(log => log.type === filterType);
    }

    // Filter by search query
    if (searchTerm) {
      filtered = filtered.filter(log => {
        const searchLower = searchTerm.toLowerCase();
        
        // Search in nurse name
        const nurseNameMatch = log.nurseName?.toLowerCase().includes(searchLower);
        
        // Search in PDL name
        const pdlName = log.patientInfo 
          ? `${log.patientInfo.last_name || ''} ${log.patientInfo.first_name || ''} ${log.patientInfo.middle_name || ''}`.toLowerCase()
          : '';
        const pdlNameMatch = pdlName.includes(searchLower);
        
        // Search in type
        const typeText = log.type === 'consultation' ? 'consultation' : log.type === 'final_remarks' ? 'final remarks' : 'health record';
        const typeMatch = typeText.includes(searchLower);
        
        // Search in action
        const actionMatch = log.action?.toLowerCase().includes(searchLower);
        
        return nurseNameMatch || pdlNameMatch || typeMatch || actionMatch;
      });
    }

    // Filter by date
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(log => {
        const logDate = new Date(log.timestamp);
        switch (dateFilter) {
          case 'today':
            return logDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return logDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return logDate >= monthAgo;
          case 'specific':
            if (specificDate) {
              const selectedDate = new Date(specificDate);
              return logDate.toDateString() === selectedDate.toDateString();
            }
            return true;
          case 'range':
            if (dateRangeFrom && dateRangeTo) {
              const fromDate = new Date(dateRangeFrom);
              const toDate = new Date(dateRangeTo);
              toDate.setHours(23, 59, 59, 999); // Include the entire end date
              return logDate >= fromDate && logDate <= toDate;
            } else if (dateRangeFrom) {
              const fromDate = new Date(dateRangeFrom);
              return logDate >= fromDate;
            } else if (dateRangeTo) {
              const toDate = new Date(dateRangeTo);
              toDate.setHours(23, 59, 59, 999);
              return logDate <= toDate;
            }
            return true;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [activityLogs, filterType, searchTerm, dateFilter, specificDate, dateRangeFrom, dateRangeTo]);

  // Stats calculations
  const stats = React.useMemo(() => ({
    totalActivities: filteredLogs.length,
    createdActivities: filteredLogs.filter(log => log.action === 'created').length,
    updatedActivities: filteredLogs.filter(log => log.action === 'updated').length,
    uniqueNurses: new Set(filteredLogs.map(log => log.nurseName).filter(Boolean)).size,
    uniquePDLs: new Set(filteredLogs.map(log => log.patientInfo?.id).filter(Boolean)).size
  }), [filteredLogs]);

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLogs = filteredLogs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filterType, searchTerm, dateFilter, specificDate, dateRangeFrom, dateRangeTo]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(`.${styles.profileSection}`)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const toggleDarkMode = (newMode) => {
    setPendingTheme(newMode);
    setShowThemeModal(true);
  };

  const confirmThemeChange = async () => {
    // Close modal immediately
    setShowThemeModal(false);
    
    // Update local state and storage
    setIsDarkMode(pendingTheme);
    localStorage.setItem('theme', pendingTheme ? 'dark' : 'light');
    
    try {
      // Update database in background
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          if (userData && userData.id) {
            await authFetch(`${process.env.REACT_APP_API_BASE_URL || 'https://api.bjmpmedicast.com'}/api/update-theme`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                user_id: userData.id,
                is_darkmode: pendingTheme ? 1 : 0
              })
            });
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    } finally {
      setPendingTheme(null);
    }
  };

  const cancelThemeChange = () => {
    setShowThemeModal(false);
    setPendingTheme(null);
  };

  return (
    <div className={`${styles.systemApp} ${isDarkMode ? '' : styles.lightMode}`}>
      {/* System Header */}
      <header className={styles.systemHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.systemLogo}>
            <img src="/BJMPRO-CAR.png" alt="BJMP CAR Logo" className={styles.systemLogoImg} />
            <div className={styles.systemTitle}>
              <h1>MediCast: BJMP Health Monitoring System</h1>
              <p>Bureau of Jail Management and Penology</p>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.systemInfo}>
            <div className={styles.systemTime}>{currentTime.toLocaleTimeString()}</div>
            <div className={styles.systemUser}>{formatDate(currentTime)}</div>
          </div>
          <div className={`${styles.profileSection} profile-section`}>
            <div className={styles.profileIcon} onClick={toggleDropdown}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16 14 12 14Z" fill="currentColor"/>
              </svg>
            </div>
            {isDropdownOpen && (
              <div className={styles.profileDropdown}>
                <div className={styles.dropdownHeader}>
                  <div className={styles.userInfo}>
                    <div className={styles.userRole}>
                      {userData?.user_type === 'unit_nurse' ? 'Unit Nurse' : 
                       userData?.user_type === 'chief_nurse' ? 'Chief Nurse' : 
                       userData?.user_type === 'warden' ? 'Warden' : 
                       userData?.user_type === 'admin' ? 'System Admin' : 
                       userData?.user_type === 'jail_officer' ? 'Jail Officer' : 'User'}
                    </div>
                    <div className={styles.userName}>
                      {userData ? 
                        `${userData.last_name ? userData.last_name.charAt(0).toUpperCase() + userData.last_name.slice(1).toLowerCase() : ''}, ${userData.first_name ? userData.first_name.charAt(0).toUpperCase() + userData.first_name.slice(1).toLowerCase() : ''} ${userData.middle_name ? userData.middle_name.charAt(0).toUpperCase() + userData.middle_name.slice(1).toLowerCase() : ''}`.trim() 
                        : 'User Name'}
                    </div>
                  </div>
                </div>
                <div className={styles.dropdownDivider}></div>
                <button className={styles.dropdownItem} onClick={() => toggleDarkMode(!isDarkMode)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {isDarkMode ? (
                      <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    )}
                  </svg>
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
                <button className={styles.dropdownItem} onClick={() => setShowChangePasswordModal(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Change Password
                </button>
                <button className={styles.dropdownItem} onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className={styles.contentWrapper}>
        <main className={styles.mainContent}>
          {/* Date Modal */}
          {isDateModalOpen && (
            <div className={styles.modalOverlay} onClick={() => setIsDateModalOpen(false)}>
              <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                  <h3>Select Date</h3>
                </div>
                <div className={styles.modalBody}>
                  <div className={styles.modalRow}>
                    <div className={styles.modalField}>
                      <label>Month</label>
                      <select value={modalMonth} onChange={(e) => {
                        const newMonth = e.target.value;
                        setModalMonth(newMonth);
                        // Update text date field
                        if (newMonth && modalDay && modalYear) {
                          setModalTextDate(`${newMonth}/${modalDay}/${modalYear}`);
                        }
                        // Reset day if current day exceeds max days in new month
                        if (modalDay) {
                          const maxDays = getDaysInMonth(newMonth, modalYear);
                          if (parseInt(modalDay) > maxDays) {
                            setModalDay('');
                            setModalTextDate('');
                          }
                        }
                      }}>
                        <option value="">Select month</option>
                        {[
                          { label: 'January', value: '01' },
                          { label: 'February', value: '02' },
                          { label: 'March', value: '03' },
                          { label: 'April', value: '04' },
                          { label: 'May', value: '05' },
                          { label: 'June', value: '06' },
                          { label: 'July', value: '07' },
                          { label: 'August', value: '08' },
                          { label: 'September', value: '09' },
                          { label: 'October', value: '10' },
                          { label: 'November', value: '11' },
                          { label: 'December', value: '12' }
                        ].map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.modalField}>
                      <label>Day</label>
                      <select value={modalDay} onChange={(e) => {
                        const newDay = e.target.value;
                        setModalDay(newDay);
                        // Update text date field
                        if (modalMonth && newDay && modalYear) {
                          setModalTextDate(`${modalMonth}/${newDay}/${modalYear}`);
                        }
                      }}>
                        <option value="">Select day</option>
                        {Array.from({length: getDaysInMonth(modalMonth, modalYear)}, (_,i)=>String(i+1).padStart(2,'0')).map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.modalField}>
                      <label>Year</label>
                      <select value={modalYear} onChange={(e) => {
                        const newYear = e.target.value;
                        setModalYear(newYear);
                        // Update text date field
                        if (modalMonth && modalDay && newYear) {
                          setModalTextDate(`${modalMonth}/${modalDay}/${newYear}`);
                        }
                        // Reset day if current day exceeds max days (affects Feb leap year)
                        if (modalDay && modalMonth) {
                          const maxDays = getDaysInMonth(modalMonth, newYear);
                          if (parseInt(modalDay) > maxDays) {
                            setModalDay('');
                            setModalTextDate('');
                          }
                        }
                      }}>
                        <option value="">Select year</option>
                        {Array.from({length: 50}, (_,i)=>String(new Date().getFullYear()-i)).map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className={styles.modalRow}>
                    <div className={styles.modalField} style={{flex:1}}>
                      <label>Type date (mm/dd/yyyy)</label>
                      <input
                        type="text"
                        placeholder="mm/dd/yyyy"
                        value={modalTextDate}
                        onChange={(e) => {
                          const val = e.target.value;
                          
                          // Handle backspace/deletion by allowing any valid partial input
                          if (val.length < modalTextDate.length) {
                            setModalTextDate(val);
                            return;
                          }
                          
                          // Allow only numbers and slashes
                          if (/^[\d/]*$/.test(val)) {
                            let formatted = val.replace(/\//g, '');
                            
                            // Validate month as we type (01-12)
                            if (formatted.length >= 1) {
                              const firstDigit = parseInt(formatted[0]);
                              if (firstDigit > 1) {
                                formatted = '0' + formatted;
                              }
                            }
                            if (formatted.length >= 2) {
                              const month = parseInt(formatted.slice(0, 2));
                              if (month > 12 || month === 0) {
                                return; // Don't allow invalid month
                              }
                              formatted = formatted.slice(0, 2) + '/' + formatted.slice(2);
                            }
                            
                            // Validate day based on month
                            if (formatted.length >= 4) {
                              const dayPart = formatted.slice(3).replace('/', '');
                              
                              if (dayPart.length >= 1) {
                                const firstDigit = parseInt(dayPart[0]);
                                if (firstDigit > 3) {
                                  return; // Day can't start with > 3
                                }
                              }
                              
                              formatted = formatted.slice(0, 5) + '/' + formatted.slice(5);
                            }
                            
                            // Validate complete day against month/year
                            if (formatted.length >= 5) {
                              const parts = formatted.split('/');
                              if (parts.length >= 2) {
                                const month = parts[0];
                                const day = parts[1];
                                const year = parts[2] || '';
                                
                                if (day.length === 2) {
                                  const dayNum = parseInt(day);
                                  const maxDays = getDaysInMonth(month, year || modalYear || new Date().getFullYear().toString());
                                  
                                  if (dayNum > maxDays || dayNum === 0) {
                                    return; // Don't allow invalid day
                                  }
                                }
                                
                                // Validate year (cannot exceed current year 2025)
                                if (year.length === 4) {
                                  const yearNum = parseInt(year);
                                  if (yearNum > 2025) {
                                    return; // Don't allow years after 2025
                                  }
                                }
                              }
                            }
                            
                            if (formatted.length <= 10) {
                              setModalTextDate(formatted);
                              
                              // Update dropdowns when text is complete
                              if (formatted.length === 10) {
                                const [mm, dd, yyyy] = formatted.split('/');
                                if (mm && dd && yyyy) {
                                  setModalMonth(mm);
                                  setModalDay(dd);
                                  setModalYear(yyyy);
                                }
                              }
                            }
                          }
                        }}
                        maxLength="10"
                      />
                    </div>
                  </div>
                  {modalError && (
                    <div className={styles.modalError}>{modalError}</div>
                  )}
                  <div className={styles.modalHelper}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <span>Select a date using the dropdowns above or type it in mm/dd/yyyy format</span>
                  </div>
                </div>
                <div className={styles.modalFooter}>
                  <button className={styles.modalBtnGhost} onClick={() => setIsDateModalOpen(false)}>Cancel</button>
                  <button
                    className={styles.modalBtn}
                    onClick={() => {
                      let final = '';
                      setModalError('');
                      if (modalTextDate) {
                        const m = /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(modalTextDate);
                        if (m) {
                          let [mm,dd,yyyy] = modalTextDate.split('/');
                          // Pad month and day with leading zeros if needed
                          mm = mm.padStart(2, '0');
                          dd = dd.padStart(2, '0');
                          
                          // Validate the date
                          const monthNum = parseInt(mm);
                          const dayNum = parseInt(dd);
                          const yearNum = parseInt(yyyy);
                          
                          if (monthNum < 1 || monthNum > 12) {
                            setModalError('Invalid month. Please enter a month between 01 and 12.');
                            return;
                          }
                          
                          const maxDays = getDaysInMonth(mm, yyyy);
                          if (dayNum < 1 || dayNum > maxDays) {
                            if (monthNum === 2 && dayNum === 29) {
                              setModalError(`February ${dayNum} is not valid for ${yearNum}. ${yearNum} is not a leap year.`);
                            } else {
                              setModalError(`Invalid day. ${monthNum === 2 ? 'February' : 'This month'} only has ${maxDays} days${monthNum === 2 ? ` in ${yearNum}` : ''}.`);
                            }
                            return;
                          }
                          
                          final = `${yyyy}-${mm}-${dd}`;
                        } else {
                          setModalError('Please enter a valid date in mm/dd/yyyy format.');
                        }
                      } else if (modalMonth && modalDay && modalYear) {
                        // Validate dropdown selections
                        const maxDays = getDaysInMonth(modalMonth, modalYear);
                        const dayNum = parseInt(modalDay);
                        if (dayNum > maxDays) {
                          const monthNum = parseInt(modalMonth);
                          if (monthNum === 2 && dayNum === 29) {
                            setModalError(`February ${dayNum} is not valid for ${modalYear}. ${modalYear} is not a leap year.`);
                          } else {
                            setModalError(`Invalid day selected.`);
                          }
                          return;
                        }
                        final = `${modalYear}-${modalMonth}-${modalDay}`;
                      } else {
                        setModalError('Please select month, day, and year.');
                      }
                      if (final) {
                        if (modalType === 'specific') {
                          setSpecificDate(final);
                        } else if (modalType === 'from') {
                          setDateRangeFrom(final);
                        } else if (modalType === 'to') {
                          setDateRangeTo(final);
                        }
                        setIsDateModalOpen(false);
                        setModalMonth(''); setModalDay(''); setModalYear(''); setModalTextDate(''); setModalType('');
                      }
                    }}
                    disabled={!(modalTextDate || (modalMonth && modalDay && modalYear))}
                  >Apply</button>
                </div>
              </div>
            </div>
          )}
          
          {/* Page Header with Back Button */}
          <div className={styles.jbrHeader}>
            <button 
              className={styles.jbrBackBtn}
              onClick={() => navigate('/dashboard')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Dashboard
            </button>
            <h1 className={styles.jbrTitle}>Activity Logs</h1>
          </div>

          {/* Filters Card */}
          <div className={styles.filtersCard}>
            <div className={styles.filterRow}>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2"/>
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Activity Type
                </label>
                <select 
                  className={styles.filterSelect}
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="all">All Activities</option>
                  <option value="consultation">Consultations Only</option>
                  <option value="health_record">Health Records Only</option>
                  <option value="final_remarks">Final Remarks Only</option>
                </select>
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Date Range
                </label>
                <select 
                  className={styles.filterSelect}
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="specific">Specific Date</option>
                  <option value="range">Date Range</option>
                </select>
              </div>

              {/* Specific Date Input */}
              {dateFilter === 'specific' && (
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                      <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                      <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Select Date
                  </label>
                  <input
                    type="text"
                    readOnly
                    className={styles.dateInput}
                    value={specificDate ? (() => {
                      const [yyyy, mm, dd] = specificDate.split('-');
                      return `${mm}/${dd}/${yyyy}`;
                    })() : ''}
                    placeholder="mm/dd/yyyy"
                    onClick={() => {
                      // Pre-fill modal with existing date if present
                      if (specificDate) {
                        const [yyyy, mm, dd] = specificDate.split('-');
                        setModalMonth(mm);
                        setModalDay(dd);
                        setModalYear(yyyy);
                        setModalTextDate(`${mm}/${dd}/${yyyy}`);
                      }
                      setModalType('specific');
                      setIsDateModalOpen(true);
                    }}
                  />
                </div>
              )}

              {/* Date Range Inputs */}
              {dateFilter === 'range' && (
                <>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                        <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                        <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      From Date
                    </label>
                    <input
                      type="text"
                      readOnly
                      className={styles.dateInput}
                      value={dateRangeFrom ? (() => {
                        const [yyyy, mm, dd] = dateRangeFrom.split('-');
                        return `${mm}/${dd}/${yyyy}`;
                      })() : ''}
                      placeholder="mm/dd/yyyy"
                      onClick={() => {
                        if (dateRangeFrom) {
                          const [yyyy, mm, dd] = dateRangeFrom.split('-');
                          setModalMonth(mm);
                          setModalDay(dd);
                          setModalYear(yyyy);
                          setModalTextDate(`${mm}/${dd}/${yyyy}`);
                        }
                        setModalType('from');
                        setIsDateModalOpen(true);
                      }}
                    />
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                        <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                        <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      To Date
                    </label>
                    <input
                      type="text"
                      readOnly
                      className={styles.dateInput}
                      value={dateRangeTo ? (() => {
                        const [yyyy, mm, dd] = dateRangeTo.split('-');
                        return `${mm}/${dd}/${yyyy}`;
                      })() : ''}
                      placeholder="mm/dd/yyyy"
                      onClick={() => {
                        if (dateRangeTo) {
                          const [yyyy, mm, dd] = dateRangeTo.split('-');
                          setModalMonth(mm);
                          setModalDay(dd);
                          setModalYear(yyyy);
                          setModalTextDate(`${mm}/${dd}/${yyyy}`);
                        }
                        setModalType('to');
                        setIsDateModalOpen(true);
                      }}
                    />
                  </div>
                </>
              )}

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                    <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Search
                </label>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search by nurse name, PDL name, type, or action..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <button className={styles.refreshBtn} onClick={() => window.location.reload()}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M23 4v6h-6M1 20v-6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Refresh
              </button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statIcon} style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <div className={styles.statValue}>{stats.totalActivities}</div>
                <div className={styles.statLabel}>Total Activities</div>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon} style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#a855f7' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <div className={styles.statValue}>{stats.createdActivities}</div>
                <div className={styles.statLabel}>Created</div>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon} style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <div className={styles.statValue}>{stats.updatedActivities}</div>
                <div className={styles.statLabel}>Updated</div>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon} style={{ background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <div className={styles.statValue}>{stats.uniqueNurses}</div>
                <div className={styles.statLabel}>Unique Nurses</div>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon} style={{ background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <div className={styles.statValue}>{stats.uniquePDLs}</div>
                <div className={styles.statLabel}>Unique PDLs</div>
              </div>
            </div>
          </div>

          {/* Activity Logs Table */}
          <div className={styles.tableCard}>
            <div className={styles.tableHeader}>
              <h3>Nurse Activity Logs</h3>
              <span className={styles.recordCount}>
                Showing {startIndex + 1}-{Math.min(endIndex, filteredLogs.length)} of {filteredLogs.length}
              </span>
            </div>
            {loading ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner}></div>
                <p>Loading activity logs...</p>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className={styles.emptyState}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <h3>No activity logs found</h3>
                <p>Try adjusting your search or filter criteria</p>
              </div>
            ) : (
              <>
                <div className={styles.tableWrapper}>
                  <table className={styles.activityTable}>
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Type</th>
                        <th>Action</th>
                        <th>Nurse Name</th>
                        <th>PDL Name</th>
                        <th>Time Ago</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentLogs.map((log) => {
                        const pdlName = log.patientInfo 
                          ? (() => {
                              const lastName = log.patientInfo.last_name ? log.patientInfo.last_name.charAt(0).toUpperCase() + log.patientInfo.last_name.slice(1).toLowerCase() : '';
                              const firstName = log.patientInfo.first_name ? log.patientInfo.first_name.charAt(0).toUpperCase() + log.patientInfo.first_name.slice(1).toLowerCase() : '';
                              const middleInitial = log.patientInfo.middle_name ? log.patientInfo.middle_name.charAt(0).toUpperCase() + '.' : '';
                              return `${lastName}, ${firstName} ${middleInitial}`.trim().replace(/,$/, '');
                            })()
                          : 'N/A';
                        
                        return (
                          <tr key={log.id} className={styles.activityRow}>
                            <td className={styles.timestamp}>
                              {formatDate(log.timestamp)}
                            </td>
                            <td>
                              <span className={`${styles.typeBadge} ${styles[log.type]}`}>
                                {log.type === 'consultation' ? 'Consultation' : log.type === 'health_record' ? 'Health Record' : 'Final Remarks'}
                              </span>
                            </td>
                            <td>
                              <span className={`${styles.actionBadge} ${log.type === 'final_remarks' ? styles.remarkAction : styles[log.action]}`}>
                                {log.type === 'final_remarks' ? log.action : (log.action === 'created' ? 'Created' : 'Updated')}
                              </span>
                            </td>
                            <td className={styles.nurseName}>
                              {log.nurseName || 'N/A'}
                            </td>
                            <td className={styles.pdlName}>{pdlName}</td>
                            <td className={styles.timeAgo}>{formatTimestamp(log.timestamp)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className={styles.pagination}>
                    <button
                      className={styles.paginationBtn}
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Previous
                    </button>

                    <div className={styles.pageInfo}>
                      Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                    </div>

                    <button
                      className={styles.paginationBtn}
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        userData={JSON.parse(localStorage.getItem('user'))}
        onSuccess={(msg) => {
          setSuccessMessage(msg);
          setShowSuccessModal(true);
        }}
        onError={(msg) => {
          setErrorMessage(msg);
          setShowErrorModal(true);
        }}
      />

      {/* Success Modal */}
      {showSuccessModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowSuccessModal(false)}>
          <div className={styles.successModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Success</h3>
            <p>{successMessage}</p>
            <button className={styles.successBtn} onClick={() => setShowSuccessModal(false)}>
              OK
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Error Modal */}
      {showErrorModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowErrorModal(false)}>
          <div className={styles.errorModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.errorIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className={styles.errorBtn} onClick={() => setShowErrorModal(false)}>
              Close
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Theme Confirmation Modal */}
      {showThemeModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={cancelThemeChange}>
          <div className={styles.themeModal} onClick={(e) => e.stopPropagation()} style={{borderColor: '#f59e0b', background: isDarkMode ? '#1e293b' : 'white'}}>
            <div className={styles.themeIcon} style={{background: 'rgba(245, 158, 11, 0.1)'}}>
              {pendingTheme ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <h3 style={{color: '#f59e0b', textAlign: 'center'}}>Change Theme</h3>
            <p style={{textAlign: 'center', color: isDarkMode ? '#cbd5e1' : '#475569'}}>Are you sure you want to switch to <strong>{pendingTheme ? 'Dark' : 'Light'} Mode</strong>?</p>
            <div style={{display: 'flex', gap: '0.75rem', width: '100%'}}>
              <button className={styles.cancelBtn} onClick={cancelThemeChange}>
                Cancel
              </button>
              <button className={styles.themeBtn} onClick={confirmThemeChange}>
                Confirm
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={cancelLogout}>
          <div className={styles.themeModal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#ef4444' }}>
            <div className={styles.themeIcon} style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="16,17 21,12 16,7" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="21" y1="12" x2="9" y2="12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 style={{ color: '#ef4444' }}>Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className={styles.cancelBtn} onClick={cancelLogout}>Cancel</button>
              <button className={styles.themeBtn} onClick={confirmLogout} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>Logout</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default SuActivityLogs;
